import React, { useState, useEffect } from "react";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import PropTypes from "prop-types";
import ReCAPTCHA from "react-google-recaptcha";
import "./css/Quote.css";

const serviceOptions = [
  { value: "gardening", label: "Gardening" },
  { value: "maintenance", label: "Maintenance" },
  { value: "seasonalplanters", label: "Seasonal Planters" },
  { value: "seasonalservice", label: "Seasonal Service" },
  { value: "chemicalapplication", label: "Chemical Application" },
  { value: "notlisted", label: "Not Listed" },
];

const animatedComponents = makeAnimated();
const G_KEY = process.env.REACT_APP_GSITE_KEY;

export default function Quote({ isOpen, onClose, preselectedService }) {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    address: "",
    zipCode: "",
    phoneNumber: "",
    message: "",
    services: [],
    company: "",
  });

  const [errors, setErrors] = useState({});
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [captchaValue, setCaptchaValue] = useState(null);
  const [characterLimit, setCharacterLimit] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const MAX_CHARACTER_LIMIT = 200;

  useEffect(() => {
    if (preselectedService) {
      const selectedOption = serviceOptions.find(
        (option) => option.label === preselectedService
      );
      if (selectedOption) {
        setFormData((prev) => ({
          ...prev,
          services: [selectedOption],
        }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        services: [],
      }));
    }
  }, [preselectedService]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (name === "message") setCharacterLimit(value.length);

    if (errors[name]) {
      const newErrors = { ...errors };
      delete newErrors[name];
      setErrors(newErrors);
    }
  };

  const handleSelectChange = (selected) => {
    setFormData({ ...formData, services: selected });
    if (errors.services) {
      const newErrors = { ...errors };
      delete newErrors.services;
      setErrors(newErrors);
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = "First name is required";
    if (!formData.lastName.trim()) newErrors.lastName = "Last name is required";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Email is not valid";
    }
    if (!formData.phoneNumber.trim()) newErrors.phoneNumber = "Phone number is required";
    if (formData.services.length === 0) newErrors.services = "Please select at least one service";
    if (!captchaValue) newErrors.captcha = "Please verify that you are human";
    if (formData.company.trim()) newErrors.company = "Try again...";

    return newErrors;
  };

  const sendEmail = async (templateParams) => {
    const fullName = `${templateParams.firstName} ${templateParams.lastName}`;
    const servicesText = templateParams.services;

    const emailHtml = `
      <h2>New Service Inquiry from ${fullName}</h2>
      <p><strong>Services Requested:</strong> ${servicesText}</p>
      <p><strong>Email:</strong> ${templateParams.email}</p>
      <p><strong>Phone:</strong> ${templateParams.phoneNumber}</p>
      ${templateParams.address ? `<p><strong>Address:</strong> ${templateParams.address}</p>` : ""}
      ${templateParams.zipCode ? `<p><strong>Zip Code:</strong> ${templateParams.zipCode}</p>` : ""}
      ${templateParams.message ? `<hr /><p><strong>Additional Notes:</strong></p><p>${templateParams.message}</p>` : ""}
    `;

    try {
      const response = await fetch("https://api.syncgr.com/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          to: "info@enchanted-gardener.com",
          from: "info@syncgr.com",
          subject: `New Service Inquiry from ${fullName} - Enchanted Gardener`,
          html: emailHtml,
          replyTo: templateParams.email,
          domain: "enchanted-gardener.com",
          contactName: fullName,
          contactEmail: templateParams.email,
          contactPhone: templateParams.phoneNumber,
          contactMessage: `Services: ${servicesText}${templateParams.message ? ` | Notes: ${templateParams.message}` : ""}`,
        }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSubmitSuccess(true);
        setSubmitError(false);
      } else {
        setSubmitError(true);
        setSubmitSuccess(false);
      }
    } catch (error) {
      console.error("Failed to send email:", error);
      setSubmitError(true);
      setSubmitSuccess(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formErrors = validate();
    if (Object.keys(formErrors).length === 0) {
      setIsSubmitting(true);
      const templateParams = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        address: formData.address,
        zipCode: formData.zipCode,
        message: formData.message,
        services: formData.services.map((service) => service.label).join(", "),
      };

      await sendEmail(templateParams);
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        address: "",
        zipCode: "",
        phoneNumber: "",
        message: "",
        services: [],
        company: "",
      });
      setCharacterLimit(0);
      setErrors({});
      setIsSubmitting(false);
    } else {
      setErrors(formErrors);
      setSubmitSuccess(false);
    }
  };

  const onCaptchaChange = (value) => setCaptchaValue(value);

  if (!isOpen) return null;

  return (
    <div className="quote-overlay" onClick={onClose}>
      <div className="quote-modal" onClick={(e) => e.stopPropagation()}>
        <button className="quote-close" onClick={onClose}>
          &times;
        </button>

        <div className="quote-header">
          <h2>Request a Quote</h2>
          <p>Fill out the form below and we'll get back to you shortly.</p>
        </div>

        <div className="quote-body">
          {submitSuccess && (
            <div className="quote-alert quote-alert-success">
              Your request has been submitted successfully! We'll be in touch soon.
            </div>
          )}
          {submitError && (
            <div className="quote-alert quote-alert-error">
              There was an error submitting your request. Please try again.
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate className="quote-form">
            {/* Honeypot */}
            <input
              type="text"
              name="company"
              value={formData.company}
              onChange={handleChange}
              style={{ display: "none" }}
              tabIndex={-1}
              autoComplete="off"
            />

            <div className="form-group">
              <label>Service Selection *</label>
              <Select
                options={serviceOptions}
                isMulti
                components={animatedComponents}
                value={formData.services}
                onChange={handleSelectChange}
                placeholder="Select services..."
                classNamePrefix="quote-select"
                className={errors.services ? "error" : ""}
              />
              {errors.services && <span className="error-message">{errors.services}</span>}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>First Name *</label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder="First name"
                  className={errors.firstName ? "error" : ""}
                />
                {errors.firstName && <span className="error-message">{errors.firstName}</span>}
              </div>
              <div className="form-group">
                <label>Last Name *</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder="Last name"
                  className={errors.lastName ? "error" : ""}
                />
                {errors.lastName && <span className="error-message">{errors.lastName}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Email address"
                  className={errors.email ? "error" : ""}
                />
                {errors.email && <span className="error-message">{errors.email}</span>}
              </div>
              <div className="form-group">
                <label>Phone *</label>
                <input
                  type="tel"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  placeholder="Phone number"
                  className={errors.phoneNumber ? "error" : ""}
                />
                {errors.phoneNumber && <span className="error-message">{errors.phoneNumber}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Address</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="Street address"
                />
              </div>
              <div className="form-group form-group-small">
                <label>Zip Code</label>
                <input
                  type="text"
                  name="zipCode"
                  value={formData.zipCode}
                  onChange={handleChange}
                  placeholder="Zip"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Additional Notes</label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Tell us about your project..."
                maxLength={200}
                rows={4}
              />
              <span className="character-count">{characterLimit}/{MAX_CHARACTER_LIMIT}</span>
            </div>

            <div className="form-group captcha-group">
              <ReCAPTCHA sitekey={G_KEY} onChange={onCaptchaChange} />
              {errors.captcha && <span className="error-message">{errors.captcha}</span>}
            </div>

            <button type="submit" className="quote-submit" disabled={isSubmitting}>
              {isSubmitting ? "Submitting..." : "Submit Request"}
            </button>
          </form>

          <div className="quote-contact">
            <p>Or reach us directly:</p>
            <div className="contact-links">
              <a href="tel:6166820808">(616) 682-0808</a>
              <span className="divider">|</span>
              <a href="mailto:info@enchanted-gardener.com">info@enchanted-gardener.com</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

Quote.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  preselectedService: PropTypes.string,
};
